import React, { useState } from 'react';
import { Calendar, Check, ChevronDown } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import FullScreenModal from '../../../components/ui/Modal';
import { cn } from '../../../components/ui/Button';
import { useTrimesters } from '../context/TrimestersContext';

const formatBrDate = (value?: string | null) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
};

interface TrimesterSelectorProps {
  className?: string;
  buttonClassName?: string;
}

const TrimesterSelector: React.FC<TrimesterSelectorProps> = ({ className, buttonClassName }) => {
  const { trimesters, currentTrimesterId, currentTrimester, setCurrentTrimesterId } = useTrimesters();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (trimesters.length === 0) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm',
          className,
          buttonClassName
        )}
      >
        <Calendar size={14} className="text-primary" />
        <span className="max-w-[140px] truncate">{currentTrimester?.label ?? 'Trimestre'}</span>
        <ChevronDown size={14} className="text-gray-400" />
      </button>

      <FullScreenModal
        open={open}
        onClose={() => setOpen(false)}
        title="Selecionar trimestre"
        subtitle="Escolha o trimestre para visualizar classes, alunos e relatórios."
      >
        <div className="space-y-3">
          {trimesters.map((trimester) => {
            const isActive = trimester.id === currentTrimesterId;

            return (
              <button
                key={trimester.id}
                type="button"
                onClick={() => {
                  setCurrentTrimesterId(trimester.id);
                  if (location.pathname.startsWith('/classes') || location.pathname.startsWith('/relatorios')) {
                    const params = new URLSearchParams(location.search);
                    params.set('trimester_id', trimester.id);
                    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
                  } else if (/^\/trimestres\/[^/]+\/revistas$/.test(location.pathname)) {
                    navigate(`/trimestres/${trimester.id}/revistas`, { replace: true });
                  }
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between rounded-2xl border p-4 text-left transition-colors',
                  isActive ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white hover:border-slate-300'
                )}
              >
                <div>
                  <p className="text-base font-semibold text-slate-900">{trimester.label}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    {formatBrDate(trimester.startDate)} até {formatBrDate(trimester.endDate)}
                  </p>
                </div>
                {isActive && (
                  <span className="rounded-full bg-primary p-1.5 text-white">
                    <Check size={14} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </FullScreenModal>
    </>
  );
};

export default TrimesterSelector;
